import { Button, Input, MediaCard, Modal } from '@sendible/design-system/src';
import { useTranslation } from 'react-i18next';
import { AdvancedSelect, Option } from '@sendible/design-system/src/components/molecules/AdvancedSelect';
import { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { MediaFileType } from '@sendible/shared-state-bridge';
import { useGetFilters } from '@sendible/frontendv2/src/models/filters';
import { useGetRecentMedia } from '@sendible/frontendv2/src/models/recentMedia';
import { MediaPreview } from '@sendible/frontendv2/src/components/MediaPreview';
import { supportedFileTypes } from '@sendible/frontendv2/src/models/medias/consts';
import { Container, Footer, FooterContent, GridContent, Header, SelectionCount } from '../styles/index.styles';
import { LoadingState } from '../LoadingState';
import { BlankState } from '../BlankState';
import { useComposeBoxContext } from '../../../composeBoxContext';

type selectedItem = {
  id: string;
  url: string;
  name: string;
  tw_media?: RecentFileType['tw_media'];
};
export const RecentlyUsedModal = () => {
  const { setIsRecentlyUsedModalOpen, uploadUrlsToCompose, attachExistingMedia, getRemainingMediaLimit } = useComposeBoxContext();
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<Option[]>([]);
  const [selectedItems, setSelectedItems] = useState<selectedItem[]>([]);
  const { t } = useTranslation(['compose_box', 'common_terms']);
  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewInitialItem, setPreviewInitialItem] = useState(0);
  const { data, isLoading: isLoadingUsers, isError: isErrorUsers } = useGetFilters(['User']);
  const teamMembers = data?.users;
  const { ref, inView } = useInView();
  const {
    data: recentMedia,
    isLoading: isLoadingRecentMedia,
    isError: isErrorRecentMedia,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetRecentMedia({ userIds: selectedTeamMembers.map((member) => member.action), filter: search });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isFetchingNextPage]);

  const recentMediaFiles = recentMedia?.pages.flatMap((page) => page?.files || []) || [];
  const isLoading = isLoadingUsers || isLoadingRecentMedia;
  const isError = isErrorUsers || isErrorRecentMedia;
  const teamMemberLabel = t('teamMember', { ns: 'compose_box' });
  const teamMembersLabel = t('teamMembers', { ns: 'compose_box' });
  const currentMaxLimit = getRemainingMediaLimit();

  const selectLabel =
    selectedTeamMembers.length > 1
      ? `${selectedTeamMembers.length} ${teamMembersLabel}`
      : selectedTeamMembers.length === 1
        ? `1 ${teamMemberLabel}`
        : teamMembersLabel;

  const handleMediaSelection = (selectedItem: selectedItem) => {
    if (!selectedItems.find((item) => item.id === selectedItem.id)) setSelectedItems((prev) => [...prev, selectedItem]);
    else {
      const newSelection = selectedItems.filter((i) => i.id !== selectedItem.id);

      setSelectedItems(newSelection);
    }
  };

  const handleSubmit = useCallback((value: string) => {
    setSelectedItems([]);
    setSearch(value);
  }, []);

  const handleKeyPress = useCallback(
    (key: string, text: string) => {
      if (key === 'Enter') {
        handleSubmit(text);
      }
    },
    [handleSubmit]
  );

  const handleImportClick = useCallback(() => {
    const toMediaFileType = (mediaTypeRaw?: string): MediaFileType => {
      const mediaType = mediaTypeRaw?.toLowerCase() ?? '';

      if (mediaType.startsWith('video')) return MediaFileType.Video;
      if (mediaType.includes('pdf') || mediaType.startsWith('application/')) return MediaFileType.Document;

      return MediaFileType.Image;
    };

    // The selection UI already stops at the limit; applying it to the combined list here too
    // means a mixed legacy/new selection can never send one item down each path (which, on
    // the bulk importer's one-media-per-row tab, would race two attaches into the same row).
    const itemsToImport = selectedItems.slice(0, currentMaxLimit);

    if (itemsToImport.length > 0) {
      const oldMediaSystemItems = itemsToImport.filter((item) => !item.tw_media).map((item) => ({ url: item.url, name: item.name }));
      const newMediaSystemItems = itemsToImport.filter((item) => item.tw_media);

      if (oldMediaSystemItems.length > 0) uploadUrlsToCompose(oldMediaSystemItems);
      attachExistingMedia(
        newMediaSystemItems.flatMap((item) => (item.tw_media?.id ? [{ id: item.tw_media.id, type: toMediaFileType(item.tw_media.media_type) }] : []))
      );

      setIsRecentlyUsedModalOpen(false);
    }
  }, [selectedItems, currentMaxLimit, uploadUrlsToCompose, attachExistingMedia, setIsRecentlyUsedModalOpen]);

  const getType = (media: RecentFileType) => {
    return media.tw_media
      ? ((Object.entries(supportedFileTypes)
          .find(([, prefix]) => prefix && media.tw_media.media_type.toLowerCase().startsWith(prefix))?.[0]
          .toLowerCase() || 'image') as Media['type'])
      : 'image';
  };

  const getIsGif = (media: RecentFileType) => {
    return media?.tw_media?.media_type.includes('gif');
  };

  const mediaList: Media[] | undefined = recentMediaFiles.map((media) => ({
    url: getType(media) === 'video' ? media.link_url : media.url_original,
    type: getType(media),
    id: media.id,
    isGif: getIsGif(media),
    name: media.file_name,
    downloadUrl: media.url_original,
  }));

  const handleMediaPreview = (id: MediaIdType | string) => {
    const mediaIndex = mediaList?.findIndex((media) => media.id === (typeof id === 'string' ? Number(id) : id));

    if (mediaIndex !== undefined && mediaIndex !== -1) {
      setPreviewInitialItem(mediaIndex);
      setShowPreview(true);
    }
  };

  const subheaderContent = (
    <Header>
      <Input
        icon="search"
        size="sm"
        label={t('search', { ns: 'common_terms' })}
        keyPress={handleKeyPress}
        value={searchValue}
        change={(value) => setSearchValue(value)}
        button={{ label: t('search') || '', click: () => handleSubmit(searchValue) }}
      />
      <AdvancedSelect
        footerButton={
          selectedTeamMembers.length > 0
            ? {
                onClick: () => setSelectedTeamMembers([]),
                label: t('clear', { ns: 'common_terms' }) as string,
              }
            : undefined
        }
        icon="users"
        id="team-members-select"
        input={{
          placeholder: t('search_teamMembers', { ns: 'compose_box' }),
          type: 'stackable',
        }}
        label={selectLabel}
        filterOnType
        onSelect={(o) => setSelectedTeamMembers([o])}
        onStackableSelect={(o) => setSelectedTeamMembers(o)}
        options={
          teamMembers?.map((user) => ({
            id: user.id,
            label: user.label || '',
            action: user.id,
          })) || []
        }
        selectedOptions={selectedTeamMembers}
        size="sm"
      />
    </Header>
  );

  return (
    <>
      <Modal
        portalDomId="layer-root"
        close={() => setIsRecentlyUsedModalOpen(false)}
        preventEscapeClose={showPreview}
        footerContent={
          <Footer>
            <FooterContent>
              <SelectionCount>{t('selection_count', { selected: selectedItems.length, limit: currentMaxLimit })}</SelectionCount>
              <Button
                appearance="primary"
                disabled={selectedItems.length === 0}
                label={selectedItems.length === 1 ? t('import_media') || '' : t('import_medias', { count: selectedItems.length }) || ''}
                onClick={handleImportClick}
                size={14}
                variation="fill"
              />
            </FooterContent>
          </Footer>
        }
        headingIcon="clock"
        headingTitle={t('recentlyUsed') as string}
        subHeaderContent={subheaderContent}
        variation="large"
      >
        <Container>
          {(!isLoading && recentMediaFiles.length === 0) || isError ? (
            <BlankState />
          ) : (
            <GridContent>
              {isLoading ? (
                <LoadingState />
              ) : (
                <>
                  {recentMediaFiles.map((media) => {
                    const isSelected = !!selectedItems.find((item) => item.id === media.id.toString());
                    const isDisabled = !isSelected && selectedItems.length >= currentMaxLimit;
                    const type = getType(media);

                    return (
                      <MediaCard
                        id={media.id.toString()}
                        key={media.id.toString()}
                        name={media.file_name}
                        open={() => handleMediaPreview(media.id.toString())}
                        isGif={getIsGif(media)}
                        select={
                          isDisabled
                            ? undefined
                            : () =>
                                handleMediaSelection({
                                  id: media.id.toString(),
                                  url: media.url_original,
                                  tw_media: media.tw_media,
                                  name: media.file_name,
                                })
                        }
                        showCheckboxWithoutHover={isSelected || selectedItems.length > 0}
                        updateDate={media.link_title}
                        uploadingText=""
                        duration={type === 'video' ? 'video' : undefined}
                        customClass={isDisabled ? 'disabled-mediacard' : ''}
                        thumbUrl={media.url_thumb}
                        type={type}
                        isSelected={isSelected}
                      />
                    );
                  })}
                  <div
                    ref={ref}
                    style={{ gridColumn: '1 / -1', height: 20, width: '100%' }}
                  />
                </>
              )}
            </GridContent>
          )}
        </Container>
      </Modal>
      {showPreview && mediaList?.length && mediaList.length > 0 && (
        <MediaPreview
          initialMediaIndex={previewInitialItem}
          mediaList={mediaList}
          onClose={() => setShowPreview(false)}
          shouldShowActions={false}
        />
      )}
    </>
  );
};
